<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ResponsiveDialog from '@/components/responsive-dialog.vue';

const { t } = useI18n();

interface ComplianceCheck {
  caregiverDisplayName: string;
  complianceCheck: {
    firstRuleText: string;
    secondRuleText: string;
    thirdRuleText: string;
  };
}

interface Props {
  modelValue: boolean;
  complianceChecks: ComplianceCheck[];
}

const props = defineProps<Props>();
const emit = defineEmits(['update:modelValue']);

function closeModal() {
  emit('update:modelValue', false);
}

const hasComplianceChecks = computed(() =>
  props.complianceChecks && props.complianceChecks.length > 0,
);
</script>

<template>
  <responsive-dialog
    max-width="700"
    persistent
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card v-if="hasComplianceChecks">
      <v-card-title class="text-h5 pa-4">
        {{ t('shiftForm.complianceCheckTitle') }}
      </v-card-title>
      <v-card-text>
        <p class="mb-4">
          {{ t('shiftForm.complianceInitialText') }}
        </p>
        <v-expansion-panels variant="accordion">
          <v-expansion-panel
            v-for="(check, index) in complianceChecks"
            :key="`compliance-check-${index}`"
          >
            <v-expansion-panel-title>
              <span class="font-weight-bold">{{ check.caregiverDisplayName }}</span>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-expansion-panels
                variant="accordion"
                class="mt-2"
              >
                <v-expansion-panel v-if="check.complianceCheck.firstRuleText">
                  <v-expansion-panel-title>
                    <span class="font-weight-bold">Semanas que superan el límite de horas</span>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    {{ check.complianceCheck.firstRuleText }}
                  </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel v-if="check.complianceCheck.secondRuleText">
                  <v-expansion-panel-title>
                    <span class="font-weight-bold">Semanas que superan el límite con días fuera de rango</span>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    {{ check.complianceCheck.secondRuleText }}
                  </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel v-if="check.complianceCheck.thirdRuleText">
                  <v-expansion-panel-title>
                    <span class="font-weight-bold">Días que superan las 10 horas</span>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    {{ check.complianceCheck.thirdRuleText }}
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          variant="text"
          @click="closeModal"
        >
          {{ t('shiftForm.complianceCheckClose') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </responsive-dialog>
</template>
<style scoped>
.v-expansion-panel-text {
  white-space: pre-line;
}
</style>
